'use client';

import { Popover } from 'flowbite-react/components/Popover';
import type { FunctionComponent } from 'react';
import { AbbUserOutline } from '@/assets/icons/components/abb-user-outline';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { Button } from '@/ui-components/button/button';
import { Datepicker } from '@/ui-components/datepicker/datepicker';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { useSharedDocumentsModalParams } from '../hooks/use-shared-documents-modal-params';
import { NewDocumentAccess } from './new-document-access';

const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

const popoverCustomTheme = {
  base: 'absolute z-20 inline-block w-max h-fit bg-white outline-none rounded-lg shadow-lg dark:bg-bg_dark',
  content: 'overflow-hidden rounded-md'
};

export const SharedDocumentsModal: FunctionComponent = () => {
  const { getSearchParams } = useNavigation();
  const { id: projectDocumentId } = getSearchParams();

  const {
    documentAccesses,
    isNew,
    isPending,
    isDatepickerOpen,
    t,
    format,
    handleDeleteButton,
    setIsDatepickerOpen,
    handleChangePeriod,
    handleChangeAccessLevel,
    setIsNew
  } = useSharedDocumentsModalParams({ projectDocumentId });

  return (
    <Modal name='SharedDocumentsModal' title={{ text: t('T_SHARE_DOCUMENT'), variant: 'h5' }} dismissible closeButton>
      <div className='absolute top-[64px] right-9'>
        <Button disabled={isNew || isPending} onClick={() => setIsNew(true)} variant='primary' sizing='small'>
          {`+ ${t('T_ADD_GUEST')}`}
        </Button>
      </div>
      <Paragraph className='mb-2' variant='text-sm'>
        {t('T_PEOPLE_TO_SHARE_WITH')}
      </Paragraph>
      <div className='max-h-[230px] min-w-[500px] h-[500px]  overflow-y-scroll scrollbar-rounded'>
        <Flexbox direction='col' className='gap-2'>
          {documentAccesses?.map((documentAccess) => {
            return (
              <div key={documentAccess.documentId}>
                <Flexbox className='gap-4' justify='between' items='center'>
                  <Flexbox items='center'>
                    <div className='w-10 h-10 rounded-full bg-n300_table_background mr-1.5 flex items-center justify-center'>
                      <AbbUserOutline fill='#666666' />
                    </div>
                    <Flexbox direction='col'>
                      <Paragraph variant='text-sm'>{documentAccess.userEmail ?? ''}</Paragraph>
                      <Flexbox items='center'>
                        {documentAccess.unlimited ? (
                          <Paragraph variant='text-xs' className='text-n500_light_text'>
                            ({t('T_UNLIMITED_INVITATION')})
                          </Paragraph>
                        ) : (
                          <Paragraph variant='text-xs' className='text-n500_light_text'>
                            ({`${format(documentAccess.startsAt, 'DD/MM/YYYY')} - ${format(documentAccess.expiresAt, 'DD/MM/YYYY')}`})
                          </Paragraph>
                        )}
                        <Popover
                          theme={popoverCustomTheme}
                          arrow={false}
                          placement='right-start'
                          open={isDatepickerOpen}
                          onOpenChange={setIsDatepickerOpen}
                          content={
                            <Datepicker
                              showUnlimitedCheckbox
                              setIsOpen={setIsDatepickerOpen}
                              showPreviousMonths={false}
                              onSelect={(value) => handleChangePeriod({ value, documentId: documentAccess.documentId })}
                              fixedStartDate={new Date()}
                              minDate={new Date()}
                              maxDate={nextYear}
                            />
                          }
                          key={'popover'}
                        >
                          <Button
                            disabled={isNew || isPending}
                            variant='text'
                            className='text-accent font-normal text-xs w-fit py-0'
                            sizing='small'
                          >
                            {t('T_EDIT_PERIOD')}
                          </Button>
                        </Popover>
                      </Flexbox>
                    </Flexbox>
                  </Flexbox>

                  {isPending && (
                    <Flexbox justify='center'>
                      <LoadingIndicator sizing='small' color='primary' />
                    </Flexbox>
                  )}

                  <Flexbox className='gap-1' items='center'>
                    <div>
                      <Dropdown
                        disabled={isNew || isPending}
                        inline
                        isMenu
                        textSize='sm'
                        arrowIcon={true}
                        label={
                          documentAccess.accessLevel && documentAccess.accessLevel === 'edit' ? (
                            <Paragraph variant='text-sm'>{t('T_CAN_EDIT')}</Paragraph>
                          ) : (
                            <Paragraph variant='text-sm'>{t('T_CAN_VIEW')}</Paragraph>
                          )
                        }
                        menuItems={[
                          {
                            value: 'edit',
                            label: <span className='text-sm w-full text-left px-3 py-2'>{t('T_CAN_EDIT')}</span>,

                            onClick: () => handleChangeAccessLevel({ documentId: documentAccess.documentId, value: 'edit' })
                          },
                          {
                            value: 'view',
                            label: <span className='text-sm w-full text-left px-3 py-2'>{t('T_CAN_VIEW')}</span>,
                            onClick: () => handleChangeAccessLevel({ documentId: documentAccess.documentId, value: 'view' })
                          }
                        ]}
                      />
                    </div>

                    <Button
                      variant='text'
                      disabled={isNew || isPending}
                      sizing='small'
                      className='font-normal text-sm w-fit text-brand'
                      onClick={() => handleDeleteButton(documentAccess.documentId)}
                    >
                      {t('T_REVOKE')}
                    </Button>
                  </Flexbox>
                </Flexbox>
              </div>
            );
          })}
        </Flexbox>
      </div>
      {isNew && <NewDocumentAccess documentDocumentId={projectDocumentId} setIsNew={setIsNew} />}
    </Modal>
  );
};
